/**
 * Media Cache Utility
 * Caches media files (images, videos) as Object URLs to avoid redundant network requests
 */

interface CacheEntry {
  objectUrl: string
  refCount: number
  mediaType: 'image' | 'video' | 'unknown'
}

class MediaCache {
  private cache: Map<string, CacheEntry> = new Map()
  private pendingRequests: Map<string, Promise<string>> = new Map()

  /**
   * Synchronously check if media is already cached
   * Returns the cached URL immediately without fetching
   */
  getCachedMediaUrlSync(sourceUrl: string | undefined): string | undefined {
    if (!sourceUrl) return undefined

    const cached = this.cache.get(sourceUrl)
    if (cached) {
      cached.refCount++
      console.log('🎯 MediaCache SYNC HIT:', sourceUrl)
      return cached.objectUrl
    }

    console.log('⚪ MediaCache SYNC MISS:', sourceUrl)
    return undefined
  }

  /**
   * Get a cached media URL or fetch and cache it
   */
  async getCachedMediaUrl(sourceUrl: string | undefined): Promise<string | undefined> {
    if (!sourceUrl) return undefined

    console.log('🔍 getCachedMediaUrl called for:', sourceUrl)
    
    // Check if already cached
    const cached = this.cache.get(sourceUrl)
    if (cached) {
      cached.refCount++
      console.log('✅ MediaCache HIT (cache size:', this.cache.size, '):', sourceUrl)
      return cached.objectUrl
    }

    // Check if there's a pending request for this URL
    const pending = this.pendingRequests.get(sourceUrl)
    if (pending) {
      console.log('⏳ MediaCache PENDING (pending size:', this.pendingRequests.size, '):', sourceUrl)
      return pending
    }

    // Create new fetch promise
    console.log('📥 MediaCache FETCH (cache size:', this.cache.size, ', pending size:', this.pendingRequests.size, '):', sourceUrl)
    const fetchPromise = this.fetchAndCacheMedia(sourceUrl)
    this.pendingRequests.set(sourceUrl, fetchPromise)
    console.log('📌 Added to pendingRequests (new size:', this.pendingRequests.size, ')')

    try {
      const objectUrl = await fetchPromise
      this.pendingRequests.delete(sourceUrl)
      console.log('✅ MediaCache STORED (cache size:', this.cache.size, '):', sourceUrl)
      return objectUrl
    } catch (error) {
      this.pendingRequests.delete(sourceUrl)
      // On error, return the original URL as fallback
      console.error('❌ Failed to cache media:', sourceUrl, error)
      return sourceUrl
    }
  }

  private async fetchAndCacheMedia(sourceUrl: string): Promise<string> {
    const response = await fetch(sourceUrl, {
      credentials: 'include'
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const blob = await response.blob()
    const objectUrl = URL.createObjectURL(blob)

    // Determine media type from blob MIME type
    const mediaType = this.determineMediaType(blob.type)

    // Store in cache with ref count
    this.cache.set(sourceUrl, {
      objectUrl,
      refCount: 1,
      mediaType
    })

    return objectUrl
  }

  private determineMediaType(mimeType: string): 'image' | 'video' | 'unknown' {
    if (mimeType.startsWith('image/')) return 'image'
    if (mimeType.startsWith('video/')) return 'video'
    return 'unknown'
  }

  /**
   * Release a cached media URL
   * When refCount reaches 0, the Object URL is kept in cache for reuse
   */
  releaseCachedMediaUrl(sourceUrl: string | undefined): void {
    if (!sourceUrl) return

    const cached = this.cache.get(sourceUrl)
    if (!cached) return

    cached.refCount--

    // Keep the cache entry even when refCount reaches 0
    // This ensures the media stays cached for subsequent uses
    // Object URLs are automatically cleaned up when the page is closed
  }

  /**
   * Clear entire cache (useful for testing or memory management)
   */
  clearCache(): void {
    this.cache.forEach(entry => {
      URL.revokeObjectURL(entry.objectUrl)
    })
    this.cache.clear()
    this.pendingRequests.clear()
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): { 
    size: number
    entries: string[]
    byType: { images: number; videos: number; unknown: number }
    } {
    const byType = { images: 0, videos: 0, unknown: 0 }
    
    this.cache.forEach(entry => {
      if (entry.mediaType === 'image') byType.images++
      else if (entry.mediaType === 'video') byType.videos++
      else byType.unknown++
    })

    return {
      size: this.cache.size,
      entries: Array.from(this.cache.keys()),
      byType
    }
  }
}

// Singleton instance
export const mediaCache = new MediaCache()

// Legacy export for backward compatibility
export const imageCache = mediaCache
